import {
  Fragment,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { BsXLg } from "react-icons/bs";
import { RELEASE_NOTES, UPCOMING } from "../data/releaseNotes";

interface Props {
  open: boolean;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Markdown 風 [text](url) リンクをパースして React ノードに変換する
function renderWithLinks(text: string): ReactNode {
  const pattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <Fragment key={key++}>{text.slice(lastIndex, match.index)}</Fragment>,
      );
    }
    nodes.push(
      <a
        key={key++}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sky-600 underline hover:text-sky-700"
      >
        {match[1]}
      </a>,
    );
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={key++}>{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}

export function ReleaseNotesModal({ open, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    // 初期フォーカスはモーダルコンテナへ移動 (見出しを screen reader が読み上げる)
    dialogRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lastFocusedRef.current?.focus();
      lastFocusedRef.current = null;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const root = dialogRef.current;
    if (!root) return;
    const focusables = Array.from(
      root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    );
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === root)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="release-notes-title"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2
            id="release-notes-title"
            className="text-base font-semibold text-slate-800"
          >
            リリースノート
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          >
            <BsXLg aria-hidden className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-3 text-sm text-slate-700">
          {UPCOMING.length > 0 && (
            <section className="mb-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                今後のアップデート
              </h3>
              <ul className="list-disc space-y-1 pl-5 text-xs text-slate-700">
                {UPCOMING.map((item, i) => (
                  <li key={i}>
                    {renderWithLinks(item.title)}
                    {item.detail && (
                      <p className="mt-0.5 text-slate-600">
                        {renderWithLinks(item.detail)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              更新履歴
            </h3>
            <ul className="space-y-3">
              {RELEASE_NOTES.map((note) => (
                <li
                  key={note.version}
                  className="rounded border border-slate-200 p-2"
                >
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-slate-800">
                      {note.version}
                    </span>
                    {note.date && (
                      <span className="text-xs text-slate-500">
                        {note.date}
                      </span>
                    )}
                  </div>
                  <ul className="list-disc space-y-1 pl-5 text-xs text-slate-700">
                    {note.changes.map((c, i) => (
                      <li key={i}>{renderWithLinks(c)}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </section>
          <p className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-600">
            要望・不具合問い合わせ先：Discord{" "}
            <span className="font-mono font-medium text-slate-800">
              non_non_53
            </span>{" "}
            まで
          </p>
        </div>
      </div>
    </div>
  );
}
